/**
 * Feed controller — §12.2, §12.3 `/feed`.
 *
 * "controllers hold UI state only (filters, selection, optimistic overrides)."
 * Server data is fetched by the routes through the router's `query()`; nothing
 * here caches a response.
 *
 * The optimistic override map is what makes dismiss feel instant and undoable
 * (§12.4): the state change is applied locally, the request goes out, and a
 * failure rolls the override back.
 */

import { createMemo, createSignal, type Accessor } from 'solid-js';
import { createStore, produce, type SetStoreFunction } from 'solid-js/store';

import type { Band, MatchState } from '@clotho/core';
import { api } from '~/api/client';

export type SortKey = 'score' | 'price' | 'new';

export interface FeedFilters {
  profileId: string | null;
  bands: Band[];
  states: MatchState[];
  sourceIds: string[];
  maxPriceUsd: number | null;
  q: string;
  sort: SortKey;
}

export const DEFAULT_FILTERS: FeedFilters = {
  profileId: null,
  // `reject` is excluded by default but selectable: §1.1 keeps gray-zone
  // results browsable rather than discarded.
  bands: ['strong', 'likely', 'stretch'],
  states: [],
  sourceIds: [],
  maxPriceUsd: null,
  q: '',
  sort: 'score',
};

export interface FeedController {
  signals: {
    filters: FeedFilters;
    selectedIndex: Accessor<number>;
    overrides: Record<string, MatchState>;
  };
  derivations: {
    /** Filters in the shape `api.matches` expects. */
    query: Accessor<Record<string, unknown>>;
    activeFilterCount: Accessor<number>;
  };
  actions: {
    setFilters: SetStoreFunction<FeedFilters>;
    toggleBand: (band: Band) => void;
    toggleState: (state: MatchState) => void;
    toggleSource: (sourceId: string) => void;
    reset: () => void;
    select: (index: number) => void;
    moveSelection: (delta: number, max: number) => void;
    setState: (matchId: string, state: MatchState) => Promise<void>;
    stateOf: (matchId: string, fallback: MatchState) => MatchState;
    clearOverride: (matchId: string) => void;
  };
}

export function createFeedController(): FeedController {
  const [filters, setFilters] = createStore<FeedFilters>({ ...DEFAULT_FILTERS });
  const [overrides, setOverrides] = createStore<Record<string, MatchState>>({});
  const [selectedIndex, setSelectedIndex] = createSignal(-1);

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  const query = createMemo(() => ({
    ...(filters.profileId ? { profileId: filters.profileId } : {}),
    ...(filters.bands.length > 0 ? { band: filters.bands } : {}),
    ...(filters.states.length > 0 ? { state: filters.states } : {}),
    ...(filters.sourceIds.length > 0 ? { sourceId: filters.sourceIds } : {}),
    ...(filters.maxPriceUsd !== null ? { maxPriceUsd: filters.maxPriceUsd } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    sort: filters.sort,
  }));

  const activeFilterCount = createMemo(() => {
    let count = 0;
    if (filters.profileId) count++;
    if (filters.bands.length !== DEFAULT_FILTERS.bands.length) count++;
    if (filters.states.length > 0) count++;
    if (filters.sourceIds.length > 0) count++;
    if (filters.maxPriceUsd !== null) count++;
    if (filters.q) count++;
    return count;
  });

  return {
    signals: { filters, selectedIndex, overrides },
    derivations: { query, activeFilterCount },
    actions: {
      setFilters,
      toggleBand: (band) => setFilters('bands', (bands) => toggle(bands, band)),
      toggleState: (state) => setFilters('states', (states) => toggle(states, state)),
      toggleSource: (sourceId) => setFilters('sourceIds', (ids) => toggle(ids, sourceId)),
      reset: () => setFilters({ ...DEFAULT_FILTERS }),
      select: setSelectedIndex,
      moveSelection: (delta, max) =>
        setSelectedIndex((current) => Math.min(Math.max(current + delta, 0), Math.max(max - 1, 0))),

      /**
       * Optimistic state change. The override is written first so the card
       * updates in the same frame; a failed request rolls it back rather than
       * leaving the UI claiming something the server did not accept.
       */
      setState: async (matchId, state) => {
        const previous = overrides[matchId];
        setOverrides(produce((draft) => void (draft[matchId] = state)));
        try {
          await api.setMatchState(matchId, state);
        } catch (error) {
          setOverrides(
            produce((draft) => {
              if (previous === undefined) delete draft[matchId];
              else draft[matchId] = previous;
            }),
          );
          throw error;
        }
      },

      stateOf: (matchId, fallback) => overrides[matchId] ?? fallback,

      clearOverride: (matchId) =>
        setOverrides(produce((draft) => void delete draft[matchId])),
    },
  };
}
